import z from 'zod'
import type { ParsedAsset, RawAsset, RawListingAsset } from './assetTypes.ts'
import { addressSchema } from '../addresses/types.ts'
import { getLatLngFromGeometry, parseGeom } from '../geography/parseGeom.ts'
import { assetDataSchema, assetMediaSchema } from './assetDataTypes.ts'
import { assetRelationDataSchema, type ParsedAssetRelation } from './assetRelationTypes.ts'
import { sourceDataSchema } from './assetSourceTypes.ts'

export function parseAssetRelation(
  relation: RawAsset['users_assets'][number] & { is_own?: boolean }
) {
  return {
    id: relation.id,
    relation_type: relation.relation_type,
    data: assetRelationDataSchema.safeParse(relation.data)?.data,
    user_id: relation.user_id,
    is_own: relation.is_own ?? false,
  } satisfies ParsedAssetRelation
}

export function parseAsset(asset: RawListingAsset | RawAsset): ParsedAsset {
  const addressRow = 'address' in asset ? asset.address?.[0] : null
  const parsedAddress = addressSchema.safeParse(addressRow?.address)?.data || null

  const geom = addressRow?.geom
    ? getLatLngFromGeometry(parseGeom(addressRow.geom as string, false))
    : null

  const relations = asset.users_assets?.map((relation) => parseAssetRelation(relation))

  const media = z.array(assetMediaSchema).safeParse(asset.media)?.data || []

  return {
    ...asset,
    data: assetDataSchema.safeParse(asset.data)?.data || undefined,
    source: sourceDataSchema.parse(asset.source),
    address: parsedAddress,
    relations,
    media,
    prices: 'prices' in asset ? asset.prices || [] : [],
    geom,
  } satisfies ParsedAsset
}

export function sanitizeProspectAssetForPublicDisplay(asset: ParsedAsset): ParsedAsset {
  if (asset.relations[0]?.relation_type !== 'prospect') {
    return asset
  }

  return {
    ...asset,
    prices: [],
    name: `${asset.address?.postal_code} ${asset.address?.city}`,
    address: null,
    geom: null,
    all_asset_ids: [],
  }
}
